'use client';

import React, { createContext, useContext } from 'react';
import type { Language } from '@/types';
import { SUPPORTED_LANGUAGES, createTranslator } from '@/lib/i18n';

type TranslateFunction = (key: string, params?: Record<string, string | number>) => string;

interface I18nContextType {
  language: Language;
  setLanguage: (lang: Language) => void;
  t: TranslateFunction;
  supportedLanguages: typeof SUPPORTED_LANGUAGES;
}

// Singleton English translator — no re-render when "language" changes
// because language no longer changes. Framework is kept around so existing
// call sites (useI18n / useTranslation / t) keep compiling; once every
// consumer is inlined, the whole thing can be removed.
const englishTranslator = createTranslator('en');

const DEFAULT_CONTEXT: I18nContextType = {
  language: 'en',
  setLanguage: () => {},
  t: englishTranslator,
  supportedLanguages: SUPPORTED_LANGUAGES,
};

const I18nContext = createContext<I18nContextType>(DEFAULT_CONTEXT);

interface I18nProviderProps {
  children: React.ReactNode;
  /** Legacy prop, ignored. Kept so callers don't break. */
  initialLanguage?: Language;
}

export function I18nProvider({ children }: I18nProviderProps) {
  return (
    <I18nContext.Provider value={DEFAULT_CONTEXT}>
      {children}
    </I18nContext.Provider>
  );
}

export function useI18n(): I18nContextType {
  return useContext(I18nContext);
}

export function useTranslation(): TranslateFunction {
  return useContext(I18nContext).t;
}
